import {
  Box,
  Typography,
  Stack,
  Grid,
  Paper,
  useTheme,
  Card,
  Avatar,
  Link as MUILink,
  Skeleton,
  MenuItem,
  IconButton,
  Menu
} from '@mui/material';
import { IoMdCard } from 'react-icons/io';
import { FaDollarSign } from 'react-icons/fa6';
import {
  useAddCardDetails,
  useDeleteCard,
  useGetAgencyBillingHistory,
  useGetCardDetails,
  useUpdateCardDetails
} from '@/apis/agency/billing/hook';
import Button from '@/components/Common/Button';
import { useGetAgencyOrganizations, useGetOrganizations } from '@/apis/agency/organizations/hook';
import IntegrationsDrawer from './IntegrationsDrawer';
import { useState } from 'react';
import { formatDollars, formatDuration, formatNumberDecimals } from '@/utils/helperFunctions';
import { useGetAgencyPackage } from '@/apis/agency/businessPackages/hook';
import { GoCircle, GoPlus } from 'react-icons/go';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import ApplyCoupon from './ApplyCoupon';
import ruPayCardLogo from '@/assets/RuPayLogo.svg';
import masterCardLogo from '@/assets/MasterCardLogo.svg';
import discoverCardLogo from '@/assets/DiscoverLogo.svg';
import dinnersClubCardLogo from '@/assets/DinersClubLogo.svg';
import eftPosCardLogo from '@/assets/EftposLogo.svg';
import linkCardLogo from '@/assets/LinkCardLogo.svg';
import americanExpressCardLogo from '@/assets/AmericanExpressLogo.svg';
import visaCardLogo from '@/assets/VisaCardLogo.svg';

const PaymentMethod = () => {
  const theme = useTheme();
  const { data: OrganizationData } = useGetOrganizations();
  const { mutate: addCard, isPending: addingCard } = useAddCardDetails();
  const { data: cardDetails, isLoading } = useGetCardDetails({
    enabled: Boolean(OrganizationData?.billing_enabled)
  });
  const { mutateAsync: deleteCard } = useDeleteCard();
  const { mutateAsync: updateCard } = useUpdateCardDetails();

  // separate menu state per card
  const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement | null; cardId: string | null }>({
    anchorEl: null,
    cardId: null
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cardId: string) => {
    setMenuState({ anchorEl: event.currentTarget, cardId });
  };

  const handleMenuClose = () => {
    setMenuState({ anchorEl: null, cardId: null });
  };

  if (isLoading) return <PaymentMethodSkeleton />;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box
          display='flex'
          flexDirection={{ xs: 'column', lg: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          gap={{ xs: 2, lg: 0 }}
        >
          <Box>
            <Typography variant='subtitle1' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: 20 } }}>
              Payment Method
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5}>
              Connect your payment method to manage your subscription
            </Typography>
          </Box>
          {Boolean(cardDetails?.data?.length) && (
            <Button
              onClick={() => addCard()}
              variant='contained'
              color='primary'
              startIcon={<GoPlus />}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              Add Card
            </Button>
          )}
        </Box>

        {cardDetails?.data?.length ? (
          <>
            {cardDetails?.data?.map((card: any) => (
              <Card
                key={card.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  boxShadow: 'none',
                  p: { xs: 1.5, lg: 2 },
                  gap: { xs: 2, sm: 0 }
                }}
              >
                <Box display='flex' alignItems='center' gap={2} sx={{ flex: 1, width: '100%' }}>
                  {getCardBrandImage(theme, card.type === 'link' ? 'link' : card.display_brand)}

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      display='flex'
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      gap={1}
                      flexWrap='wrap'
                    >
                      {card.type === 'link' ? (
                        <Typography fontWeight={500}>{card.email}</Typography>
                      ) : (
                        <Typography fontWeight={500}>**** **** **** {card?.last4}</Typography>
                      )}

                      {card?.default && (
                        <Typography
                          px={1}
                          py={0.2}
                          sx={{ background: theme.palette.primary.light, borderRadius: 5 }}
                          color='primary'
                          fontSize={14}
                          fontWeight={500}
                        >
                          Default
                        </Typography>
                      )}
                    </Box>
                    {card.type !== 'link' && (
                      <Typography variant='body2' color='text.secondary'>
                        Expires {card?.exp_month}/{card?.exp_year}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {!card?.default && (
                  <>
                    {/* Menu Button */}
                    <IconButton color='primary' edge='start' onClick={(e) => handleMenuOpen(e, card.id)}>
                      <BiDotsVerticalRounded />
                    </IconButton>

                    {/* Menu */}
                    <Menu
                      anchorEl={menuState.anchorEl}
                      open={menuState.cardId === card.id}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      slotProps={{
                        paper: { sx: { width: 205, boxShadow: '0px 2px 8px rgba(0,0,0,0.12)' } }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          updateCard(card?.id);
                          handleMenuClose();
                        }}
                      >
                        <GoCircle style={{ marginRight: 8 }} />
                        Set as Default
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          deleteCard(card?.id);
                          handleMenuClose();
                        }}
                        sx={{ color: 'red' }}
                      >
                        <RiDeleteBin6Line style={{ marginRight: 8, color: 'red' }} />
                        Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Card>
            ))}
          </>
        ) : (
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            p={{ xs: 1.5, lg: 2 }}
            gap={{ xs: 2, sm: 0 }}
            sx={{
              border: '1px dashed #ddd',
              borderRadius: 2
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center' sx={{ flex: 1 }}>
              <Avatar
                sx={{ bgcolor: theme.palette.primary.light, width: { xs: 36, lg: 40 }, height: { xs: 36, lg: 40 } }}
              >
                <IoMdCard color={theme.palette.primary.dark} />
              </Avatar>
              <Typography sx={{ fontSize: { xs: '0.875rem', lg: '1rem' } }}>
                No payment method connected <br />
                <Typography
                  component='span'
                  variant='body2'
                  color='text.secondary'
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  Add a payment method to continue using our services
                </Typography>
              </Typography>
            </Stack>
            <Button
              variant='contained'
              isPending={addingCard}
              size='small'
              startIcon={<IoMdCard />}
              sx={{
                borderRadius: 5,
                paddingX: { xs: 2, lg: 3 },
                width: { xs: '100%', sm: 'auto' }
              }}
              onClick={() => addCard()}
            >
              Connect Card
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

const PackagePricing = () => {
  const theme = useTheme();
  const { data: OrganizationData, isLoading: OrganizationDataLoading } = useGetAgencyOrganizations();
  const { data: OrganizationPackageData, isLoading } = useGetAgencyPackage(OrganizationData?.organization_plan_id);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (OrganizationDataLoading || isLoading) return <PackagePricingSkeleton />;
  return (
    <>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, borderRadius: 2, height: '100%' }}>
        <Box
          display='flex'
          flexDirection='column'
          gap={{ xs: 1.5, lg: 1.85 }}
          height='100%'
          justifyContent='space-between'
        >
          <Box mb={1} borderBottom='1px solid #ECECEC'>
            <Box display='flex' sx={{ fontSize: { xs: 16, lg: 18 } }} flexDirection='row' alignItems='center' gap={1}>
              <IoMdCard color={theme.palette.primary.main} />
              <Typography fontWeight={500} color='primary' sx={{ fontSize: { xs: '1rem', lg: '1.125rem' } }}>
                Current Package Pricing
              </Typography>
            </Box>
            <Typography variant='body2' color='secondary' mb={2} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Your subscription details & features
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Monthly Partnership Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.base_price)}
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Standard Call Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.per_min_charge)}/min
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Standard Warm Call Charges
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.warm_call_charge)}/min
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              AI Chat Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.per_chat_charge, 3)}/msg
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Multilingual Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.multilingual_charge_per_min)}/min
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Storage Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.storage_charge_per_gb)}/GB
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              AI Workflow
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.ai_workflow_charge_per_run)}/run
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Premium Support Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.premium_support_per_location)}/Location
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Per Location Charge
            </Typography>
            <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              {formatDollars(OrganizationPackageData?.price_details?.per_location_charge)}
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
            <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
              Integrations
            </Typography>
            <MUILink
              component='button'
              onClick={() => setDrawerOpen(true)}
              color='primary'
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: { xs: '12px', lg: '0.875rem' }
              }}
            >
              View pricing details
            </MUILink>
          </Box>
        </Box>
      </Paper>

      <IntegrationsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
};

const CurrentBill = ({ BillingData, isLoading }: any) => {
  const theme = useTheme();

  const getAppliedCouponDetails = () => {
    const couponDetails = BillingData?.coupon_discount;
    return (
      <Box display='flex' justifyContent='space-between' mb={2} color='#077F13' flexWrap='wrap' gap={0.5}>
        {couponDetails.discount_type === 'percentage' ? (
          <Typography sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Discount ({couponDetails.discount_value}% off)
          </Typography>
        ) : (
          <Typography sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Discount ({formatDollars(couponDetails.discount_value)} off)
          </Typography>
        )}
        <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
          -{formatDollars(couponDetails.discount_amount)}
        </Typography>
      </Box>
    );
  };

  if (isLoading) return <CurrentBillSkeleton />;
  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, borderRadius: 2 }}>
      <Box display='flex' flexDirection='column' gap={{ xs: 1.5, lg: 2 }}>
        <Box mb={1} pb={1} borderBottom='1px solid #ECECEC'>
          <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
            <FaDollarSign color={theme.palette.primary.main} />
            <Typography
              variant='subtitle1'
              fontWeight={500}
              color='primary'
              sx={{ fontSize: { xs: '1rem', lg: '1.125rem' } }}
            >
              Current Month Bill
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
            gutterBottom
            sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
          >
            {BillingData?.month} billing summary
          </Typography>
        </Box>

        <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Partnership Charge
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.base_amount)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Standard Call Minute Charges
            <Box color='black' component='span' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({formatDuration(BillingData?.per_min_usage?.quantity) || 0} x{' '}
              {formatDollars(BillingData?.per_min_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.per_min_usage?.estimated_amount)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Standard Warm Call Minute Charges
            <Box color='black' component='span' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({formatDuration(BillingData?.warm_call_per_min_usage?.quantity) || 0} x{' '}
              {formatDollars(BillingData?.warm_call_per_min_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.warm_call_per_min_usage?.estimated_amount)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            AI Chat Charges
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({BillingData?.per_chat_usage?.quantity} x {formatDollars(BillingData?.per_chat_usage?.unit_amount, 3)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.per_chat_usage?.estimated_amount)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Multilingual Charges
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({formatDuration(BillingData?.multilingual_usage?.quantity)} x{' '}
              {formatDollars(BillingData?.multilingual_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.multilingual_usage?.estimated_amount)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Storage Charges
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({formatNumberDecimals(BillingData?.storage_usage?.quantity, 4)} GB x{' '}
              {formatDollars(BillingData?.storage_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.storage_usage?.estimated_amount, 4)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            AI Workflow
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({formatNumberDecimals(BillingData?.ai_workflow_usage?.quantity, 0)} x{' '}
              {formatDollars(BillingData?.ai_workflow_usage?.unit_amount)} run)
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.ai_workflow_usage?.estimated_amount, 4)}
          </Typography>
        </Box>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Premium Support Charges
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({BillingData?.premium_support_usage?.quantity} x{' '}
              {formatDollars(BillingData?.premium_support_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.premium_support_usage?.estimated_amount)}
          </Typography>
        </Box>
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          mb={0.5}
          gap={0.5}
        >
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Location Charges
            <Box component='span' color='black' ml={1} sx={{ fontSize: { xs: 11, lg: 14 } }}>
              ({BillingData?.per_location_usage?.quantity} x{' '}
              {formatDollars(BillingData?.per_location_usage?.unit_amount)})
            </Box>
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.per_location_usage?.estimated_amount)}
          </Typography>
        </Box>

        <Box display='flex' justifyContent='space-between' mb={0.5} flexWrap='wrap' gap={0.5}>
          <Typography color='secondary' sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            Integration Charges
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
            {formatDollars(BillingData?.total_integration_amount)}
          </Typography>
        </Box>

        {BillingData?.coupon_discount?.is_active && BillingData?.coupon_discount?.code && getAppliedCouponDetails()}

        <Box display='flex' justifyContent='space-between' pt={1} borderTop='1px solid #ECECEC' mt={1}>
          <Typography color='secondary' fontWeight='bold' sx={{ fontSize: { xs: '14px', lg: '1rem' } }}>
            Total
          </Typography>
          <Typography fontWeight='bold' sx={{ fontSize: { xs: '14px', lg: '1rem' } }}>
            {formatDollars(BillingData?.total_amount)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default function BillingDetails() {
  const { data: BillingData, isLoading } = useGetAgencyBillingHistory();
  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <ApplyCoupon appliedCouponDetails={BillingData?.coupon_discount} isLoading={isLoading} />
      <PaymentMethod />
      <Grid container spacing={{ xs: 2, lg: 2 }}>
        <Grid item xs={12} md={6}>
          <PackagePricing />
        </Grid>
        <Grid item xs={12} md={6}>
          <CurrentBill BillingData={BillingData} isLoading={isLoading} />
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------- Skeleton Components ---------------- //
function PaymentMethodSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Skeleton variant='text' width={160} height={20} />
          <Skeleton variant='text' width={240} height={16} />
        </Box>
        <Card
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
            bgcolor: '#f9fafb',
            boxShadow: 'none',
            p: 2
          }}
        >
          <Box display='flex' alignItems='center' gap={2}>
            <Skeleton variant='circular' width={40} height={40} />
            <Box>
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={100} height={16} />
            </Box>
          </Box>
          <Skeleton variant='rounded' width={80} height={32} />
        </Card>
      </Stack>
    </Paper>
  );
}

function PackagePricingSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Box display='flex' flexDirection='column' gap={1.5}>
        <Skeleton variant='text' width={200} height={24} />
        <Skeleton variant='text' width={260} height={18} />

        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} display='flex' justifyContent='space-between' mb={0.5}>
            <Skeleton variant='text' width={140} height={18} />
            <Skeleton variant='text' width={80} height={18} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function CurrentBillSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
      <Box display='flex' flexDirection='column' gap={1}>
        <Skeleton variant='text' width={180} height={20} />
        <Skeleton variant='text' width={220} height={16} />

        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Box key={i} display='flex' justifyContent='space-between' mb={0.5}>
            <Skeleton variant='text' width={160} height={18} />
            <Skeleton variant='text' width={80} height={18} />
          </Box>
        ))}

        <Box display='flex' justifyContent='space-between' mt={1}>
          <Skeleton variant='text' width={60} height={20} />
          <Skeleton variant='text' width={80} height={20} />
        </Box>
      </Box>
    </Paper>
  );
}

export const getCardBrandImage = (theme: any, cardType: string) => {
  switch (cardType?.toLowerCase()) {
    case 'rupay':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={ruPayCardLogo} />
        </Avatar>
      );
    case 'visa':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={visaCardLogo} />
        </Avatar>
      );
    case 'mastercard':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={masterCardLogo} />
        </Avatar>
      );
    case 'discover':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={discoverCardLogo} />
        </Avatar>
      );
    case 'dinnersclub':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={dinnersClubCardLogo} />
        </Avatar>
      );
    case 'eftpos':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={eftPosCardLogo} />
        </Avatar>
      );
    case 'americanexpress':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={americanExpressCardLogo} />
        </Avatar>
      );
    case 'link':
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <img src={linkCardLogo} />
        </Avatar>
      );
    default:
      return (
        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
          <IoMdCard color={theme.palette.primary.dark} />
        </Avatar>
      );
  }
};
